import React, { useRef, useState } from "react";
import { Grid, LinearProgress, Paper } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { MdCheckCircle, MdOutlineFileUpload } from "react-icons/md";
import { uploadRequestFileApi } from "../../services/RequestAccess/RequestAccess";
import { triggerToast } from "../../utils/toast";
import * as styles from "../../pages/ApplicationAccess/UploadeRequest/HandleFileSelect.module.scss";
import Typography from "../Common/CprimeText";

const FileUploader = ({
  setIsSubmitEnabled,
  step,
  setStep,
  appId,
}: {
  setIsSubmitEnabled: (params: any) => void;
  step: string;
  setStep: any;
  appId: any;
}) => {
  const scssClass = styles as any;
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [apiCallStatus, setApiCallStatus] = useState<string>("");
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [fileName, setFileName] = useState<string>("");
  const [isError, setError] = useState<any>("");
  const [hasError, setHasError] = useState<any>("");

  const resetUpload = () => {
    setUploadPercentage(0);
    setFileName("");
    setStep("upload");
  };

  const handleFile = async (file: File) => {
    if (
      !file ||
      (!file.name?.endsWith(".xlsx") && !file.name?.endsWith(".json"))
    ) {
      triggerToast("Please upload a valid file (.xlsx or .json)", "error");
      return;
    }

    const fileSize = file?.size ?? 0;
    if (fileSize > 800 * 1024) {
      const fileSizeMB = (fileSize / 1024 / 1024).toFixed(2);
      triggerToast(
        "File size exceeds 800KB. Please upload a smaller file.",
        "error",
      );
      setHasError(
        `File exceeds ${fileSizeMB} MB. Please upload a smaller file.`,
      );
      if (fileInputRef?.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    try {
      setStep("processing");
      setUploadPercentage(0);
      setFileName(file?.name);
      setError("");

      // Upload the file
      const formData = new FormData();
      formData?.append("file", file);
      setApiCallStatus("uploading");
      const res = await uploadRequestFileApi(
        formData,
        (progressEvent: any) => {
          const loaded = progressEvent?.loaded ?? 0;
          const total = progressEvent?.total ?? 1;
          const percent = Math.round((loaded * 100) / total);
          setUploadPercentage(percent);
        },
        "promote",
        appId,
      );

      if (res?.status === "FAILURE") {
        setApiCallStatus("Failed");
        setError(
          Array.isArray(res?.message?.validationErrors)
            ? res?.message?.validationErrors?.map((err: any) => err?.message)
            : typeof res?.message === "string"
              ? res?.message
              : "File upload failed",
        );
        setStep("done");
        triggerToast(
          res?.message?.validationErrors?.[0]?.message || "File upload failed",
          "error",
        );
      } else {
        setIsSubmitEnabled(res?.data?.uploadId);
        setApiCallStatus("Success");
        setStep("done");
        triggerToast(
          res?.message?.validationErrors?.[0]?.message ||
            "File uploaded successfully",
          "success",
        );
      }
    } catch (error) {
      setApiCallStatus("Failed");
      triggerToast("Upload error", "error");
      return;
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e?.preventDefault();
    const file = e?.dataTransfer?.files[0];
    handleFile(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0] as File;
    handleFile(file);
  };

  return (
    <div>
      {step === "upload" ? (
        <div className={scssClass?.font14}>
          Limit one file. Max file size is 800kb. Supported file types are .xlsx
          and .json.
        </div>
      ) : (
        <></>
      )}
      {/* STEP 1: File Upload */}
      {step === "upload" && (
        <>
          <Grid
            container
            marginTop={2}
            alignItems="center"
            justifyContent="center"
            data-testid="inputFileContainer"
            onClick={() => fileInputRef?.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => e?.preventDefault()}
            className={`${scssClass?.fileDragDropContainer} ${hasError ? scssClass?.fileError : ""}`}
          >
            <input
              type="file"
              data-testid="inputFileId"
              accept=".xlsx, .json"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />
            <div
              style={{
                display: "grid",
                textAlign: "center",
                lineHeight: "20px",
              }}
            >
              <span>
                <MdOutlineFileUpload
                  style={{ fontSize: "20px", color: "#2469FF" }}
                />
              </span>
              <span style={{ color: "#2469FF" }}>Choose a file</span>
              <span>or drag and drop files here.</span>
            </div>
          </Grid>
          {hasError ? (
            <div className={scssClass?.hasError}>{hasError}</div>
          ) : null}
        </>
      )}

      {/* STEP 2: Progress + Table */}
      {(step === "processing" || step === "done") && (
        <>
          <div
            style={{
              marginTop: "16px",
              position: "relative",
              background: "#F1F2F4",
              padding: "1rem",
              borderRadius: "3px",
            }}
          >
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography variant="body2">{fileName}</Typography>
              </Grid>
              <Grid item display="flex" gap="4px">
                {step === "done" ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <Typography variant="body2">100%</Typography>
                    <MdCheckCircle
                      style={{ color: "green", fontSize: "16px" }}
                    />
                  </div>
                ) : (
                  <Typography variant="body2" style={{ color: "#555" }}>
                    {uploadPercentage > 0 && uploadPercentage < 100
                      ? `${uploadPercentage}%`
                      : apiCallStatus === "Failed"
                        ? apiCallStatus
                        : uploadPercentage === 100 &&
                            apiCallStatus === "uploading"
                          ? "Finishing..."
                          : ""}
                  </Typography>
                )}
                <CloseIcon
                  data-testid="closebutton"
                  fontSize="small"
                  onClick={resetUpload}
                  style={{ cursor: "pointer" }}
                />
              </Grid>
            </Grid>

            <div style={{ position: "relative", marginTop: "4px" }}>
              <LinearProgress
                variant="determinate"
                value={uploadPercentage}
                sx={{
                  height: "4px",
                  backgroundColor: "#fff",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: step === "done" ? "#4caf50" : "#000",
                  },
                }}
              />
            </div>
          </div>
          {isError?.length > 0 && (
            <Typography sx={{ color: "red", marginTop: "8px" }}>
              {Array.isArray(isError)
                ? isError?.map((error, index) => (
                    <span key={index} style={{ display: "block" }}>
                      {error}
                    </span>
                  ))
                : typeof isError === "string"
                  ? isError
                  : "An error occurred during file upload."}
            </Typography>
          )}
        </>
      )}
    </div>
  );
};

export default FileUploader;
